import { useState, useCallback, useRef } from "react";
import { speakTts, cancelTtsPlayback, type TtsContext } from "@/lib/tts";

export function useTTS() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [charIndex, setCharIndex] = useState(-1);
  const speakIdRef = useRef(0);

  const speak = useCallback(async (script: string, context?: TtsContext): Promise<void> => {
    if (!script.trim()) return;
    const id = ++speakIdRef.current;
    setLastError(null);
    setIsSpeaking(true);
    setCharIndex(-1);
    try {
      await speakTts(script, context, (ci) => {
        if (id === speakIdRef.current) setCharIndex(ci);
      });
    } catch (error) {
      if (id !== speakIdRef.current) return;
      setLastError(error instanceof Error ? error.message : "Audio failed to play");
    } finally {
      if (id === speakIdRef.current) {
        setIsSpeaking(false);
        setCharIndex(-1);
      }
    }
  }, []);

  const stop = useCallback(() => {
    speakIdRef.current++;
    cancelTtsPlayback();
    setIsSpeaking(false);
    setCharIndex(-1);
  }, []);

  return { speak, isSpeaking, stop, lastError, charIndex };
}
